/* eslint-disable */
/**
 * POSDashboard - Peace of Sleep Dashboard
 * Action-focused layout with notifications, quick actions, and forward-looking alerts
 * Gold accent theme for POS branding
 *
 * Phase 2: Access level aware - shows different actions for CMB staff vs hotel managers
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatMonthShort } from '../../../utils/formatters';
import { useAuth } from '../../../contexts/AuthContext';
import { usePOS } from '../../../contexts/POSContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import SearchSelect from '../../../components/SearchSelect';
import ErrorDisplay from '../../../components/ErrorDisplay';
import POSBatchServiceModal from '../../pos/components/POSBatchServiceModal';

// Format date to YYYY-MM-DD using LOCAL time (not UTC)
// CRITICAL: Do NOT use toISOString() as it converts to UTC and shifts dates by timezone offset
function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// POS Brand Colors
import {
  POS_GOLD,
  ROOM_STATUS_CONFIG as STATUS_CONFIG_TYPED,
  calculateRoomStatus as calcStatus,
} from '../../../config/posRoomStatus';

// Alias for backward compat with 'pending' key (used for runtime status lookups)
const _STATUS_CONFIG = { ...STATUS_CONFIG_TYPED, pending: STATUS_CONFIG_TYPED['not-serviced'] };
void _STATUS_CONFIG;

function POSDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { supabase } = useAuth();
  const {
    userProperties,
    propertiesLoading,
    selectedPropertyId,
    setSelectedPropertyId,
    selectedProperty,
    hasMultipleProperties,
    isCMBStaff,
    isHotelManager,
    canRequestService,
    accessLevel
  } = usePOS();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('pos_dashboard');

  // In POS portal context, hotel managers bypass Sentinel permission checks
  // (they have property-level access via hosp_property_managers, not permission profiles)
  const effectiveCanAccess = isHotelManager ? true : canAccess;
  const effectivePermissionLoading = isHotelManager ? false : permissionLoading;

  // Context-aware routing: POS portal uses /pos/:slug/services, admin uses /pos-services
  const slug = selectedProperty?.property_code?.toLowerCase() || (selectedProperty as any)?.slug || '';
  const isPOSPortal = window.location.pathname.startsWith('/pos/');
  const servicesPath = isPOSPortal && slug ? `/pos/${slug}/services` : '/pos-services';
  const propertiesPath = isPOSPortal && slug ? `/pos/${slug}/properties` : '/pos-properties';

  // State - use userProperties from context
  const [rooms, setRooms] = useState<Record<string, any>[]>([]);
  const [serviceRequests, setServiceRequests] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Alerts state - forward-looking notifications
  const [alerts, setAlerts] = useState<Record<string, any>[]>([]);
  const [showBatchServiceModal, setShowBatchServiceModal] = useState(false);

  // Handle property query param for deep linking
  useEffect(() => {
    const propertyParam = searchParams.get('property');
    if (propertyParam && userProperties.length > 0 && !propertiesLoading) {
      // Find property by slug or ID
      const targetProperty = userProperties.find(
        p => (p as any).slug === propertyParam || p.id === propertyParam
      );
      if (targetProperty && targetProperty.id !== selectedPropertyId) {
        setSelectedPropertyId(targetProperty.id);
        // Clear the query param after setting
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, userProperties, propertiesLoading, selectedPropertyId, setSelectedPropertyId, setSearchParams]);

  // Load data when context is ready
  useEffect(() => {
    if (!permissionLoading && canAccess && !propertiesLoading && userProperties.length > 0) {
      loadData();
    } else if (!propertiesLoading && userProperties.length === 0) {
      setLoading(false);
    }
  }, [permissionLoading, canAccess, propertiesLoading, userProperties]);

  // Load rooms and service requests for user's properties
  const loadData = async () => {
    if (userProperties.length === 0) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Get property IDs from context
      const propertyIds = userProperties.map(p => p.id);

      // Load rooms for user's properties
      const { data: roomData, error: roomError } = await supabase
        .from('hosp_rooms')
        .select('*')
        .in('property_id', propertyIds)
        .eq('status', 'active')
        .limit(10000);

      if (roomError) throw roomError;
      setRooms(roomData || []);

      // Load upcoming service requests for user's properties
      const today = formatLocalDate(new Date());
      const { data: reqData, error: reqError } = await supabase
        .from('hosp_service_requests')
        .select('*')
        .in('property_id', propertyIds)
        .gte('requested_date', today)
        .in('status', ['pending', 'confirmed'])
        .order('requested_date')
        .limit(10);

      if (reqError) throw reqError;
      setServiceRequests(reqData || []);

      // Generate alerts based on room statuses and contract data
      const generatedAlerts = [];

      // Check for overdue rooms
      const overdueRooms = (roomData || []).filter(room => {
        const property = userProperties.find(p => p.id === room.property_id);
        const tier = property?.hospitality_tier || 'assured';
        const status = calculateRoomStatus(room, tier);
        return status === 'overdue';
      });

      if (overdueRooms.length > 0) {
        generatedAlerts.push({
          id: 'overdue-rooms',
          type: 'danger',
          icon: '⚠️',
          title: `${overdueRooms.length} Room${overdueRooms.length > 1 ? 's' : ''} Overdue`,
          message: 'Service is overdue and needs immediate attention.',
          action: { label: 'Book Service', path: servicesPath }
        });
      }

      // Check for rooms due soon (within 30 days)
      const dueSoonRooms = (roomData || []).filter(room => {
        const property = userProperties.find(p => p.id === room.property_id);
        const tier = property?.hospitality_tier || 'assured';
        const status = calculateRoomStatus(room, tier);
        return status === 'due-soon';
      });

      if (dueSoonRooms.length > 0) {
        generatedAlerts.push({
          id: 'due-soon-rooms',
          type: 'warning',
          icon: '📅',
          title: `${dueSoonRooms.length} Room${dueSoonRooms.length > 1 ? 's' : ''} Due Soon`,
          message: 'Certification expires within 30 days.',
          action: { label: 'Schedule Service', path: servicesPath }
        });
      }

      // Check for contract expiry (CMB staff or owner only)
      userProperties.forEach(prop => {
        const propAny = prop as any;
        if (propAny.contract_end_date) {
          const endDate = new Date(propAny.contract_end_date);
          const today = new Date();
          const daysUntil = Math.floor((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

          if (daysUntil <= 30 && daysUntil > 0) {
            generatedAlerts.push({
              id: `contract-${prop.id}`,
              type: 'info',
              icon: '📋',
              title: `Contract Expiring Soon`,
              message: `${prop.property_name} contract expires in ${daysUntil} days.`,
              action: isCMBStaff ? { label: 'View Property', path: propertiesPath } : null
            });
          } else if (daysUntil <= 0) {
            generatedAlerts.push({
              id: `contract-expired-${prop.id}`,
              type: 'danger',
              icon: '⛔',
              title: 'Contract Expired',
              message: `${prop.property_name} contract has expired.`,
              action: isCMBStaff ? { label: 'View Property', path: propertiesPath } : null
            });
          }
        }
      });

      // Check for pending service requests that need confirmation
      const pendingRequests = (reqData || []).filter(r => r.status === 'pending');
      if (isCMBStaff && pendingRequests.length > 0) {
        generatedAlerts.push({
          id: 'pending-requests',
          type: 'info',
          icon: '📨',
          title: `${pendingRequests.length} Pending Request${pendingRequests.length > 1 ? 's' : ''}`,
          message: 'Service requests awaiting confirmation.',
          action: { label: 'Review Requests', path: servicesPath }
        });
      }

      setAlerts(generatedAlerts);
      setLoading(false);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Calculate room status — delegates to shared utility
  const calculateRoomStatus = (room: Record<string, any>, propertyTier: string): string => {
    const status = calcStatus(room.last_service_date, propertyTier);
    return status === 'not-serviced' ? 'pending' : status;
  };

  // Calculate summary stats
  const stats = useMemo(() => {
    const totalProperties = userProperties.length;
    const totalRooms = rooms.length;
    const upcomingBookings = serviceRequests.length;

    // Calculate room statuses
    let certified = 0, dueSoon = 0, overdue = 0;
    rooms.forEach(room => {
      const property = userProperties.find(p => p.id === room.property_id);
      const tier = property?.hospitality_tier || 'assured';
      const status = calculateRoomStatus(room, tier);
      if (status === 'certified') certified++;
      else if (status === 'due-soon') dueSoon++;
      else if (status === 'overdue') overdue++;
    });

    return { totalProperties, totalRooms, upcomingBookings, certified, dueSoon, overdue };
  }, [userProperties, rooms, serviceRequests]);

  // Permission check (hotel managers bypass — they use property-level access)
  if (effectivePermissionLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p>Loading...</p>
      </div>
    );
  }

  if (!effectiveCanAccess) {
    return <AccessDenied />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={loadData} />;
  }

  return (
    <div style={styles.container}>
      {/* Header with Property Selector and Primary Action */}
      <div style={styles.dashboardHeader}>
        <div style={styles.headerLeft}>
          {/* Property Selector */}
          {hasMultipleProperties ? (
            <SearchSelect
              width="inline"
              options={[
                { value: '', label: 'All Properties' },
                ...userProperties.map(p => ({ value: p.id, label: p.property_name })),
              ]}
              value={selectedPropertyId}
              onChange={setSelectedPropertyId}
            />
          ) : userProperties.length === 1 ? (
            <div style={{
              ...styles.propertySelect,
              background: `${POS_GOLD}10`,
              border: `2px solid ${POS_GOLD}40`,
              cursor: 'default'
            }}>
              {userProperties[0]?.property_name}
            </div>
          ) : null}
          {/* Access level indicator for hotel managers */}
          {isHotelManager && accessLevel && (
            <span style={styles.accessBadge}>
              {accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1)}
            </span>
          )}
        </div>
        {/* Primary action button - Request Service */}
        {canRequestService && selectedPropertyId && (
          <button
            style={styles.primaryActionBtn}
            onClick={() => setShowBatchServiceModal(true)}
          >
            + Request Service
          </button>
        )}
      </div>

      {/* Loading state */}
      {loading ? (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p>Loading dashboard...</p>
        </div>
      ) : userProperties.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>🏨</div>
          <h3>{isHotelManager ? 'No Properties Linked' : 'No Properties Yet'}</h3>
          <p>
            {isHotelManager
              ? 'Contact CleanMyBed to get your property linked to your account.'
              : 'Get started by adding your first property to the Peace of Sleep programme.'}
          </p>
          {isCMBStaff && (
            <Link to={propertiesPath} style={styles.emptyStateBtn}>
              + Add Property
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Summary Stats */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.totalProperties}</div>
              <div style={styles.statLabel}>Properties</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.totalRooms}</div>
              <div style={styles.statLabel}>Rooms</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.upcomingBookings}</div>
              <div style={styles.statLabel}>Upcoming Bookings</div>
            </div>
            <div style={{ ...styles.statCard, ...styles.statCardSuccess }}>
              <div style={styles.statValue}>{stats.certified}</div>
              <div style={styles.statLabel}>Certified</div>
            </div>
            <div style={{ ...styles.statCard, ...styles.statCardWarning }}>
              <div style={styles.statValue}>{stats.dueSoon}</div>
              <div style={styles.statLabel}>Due Soon</div>
            </div>
            <div style={{ ...styles.statCard, ...styles.statCardDanger }}>
              <div style={styles.statValue}>{stats.overdue}</div>
              <div style={styles.statLabel}>Overdue</div>
            </div>
          </div>

          {/* Alerts/Notifications Section - moved below stats */}
          {alerts.length > 0 && (
            <div style={styles.alertsSection}>
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  style={{
                    ...styles.alertCard,
                    borderLeftColor: alert.type === 'danger' ? 'var(--color-error)' :
                                     alert.type === 'warning' ? 'var(--color-warning)' : 'var(--color-info)'
                  }}
                >
                  <span style={styles.alertIcon}>{alert.icon}</span>
                  <div style={styles.alertContent}>
                    <div style={styles.alertTitle}>{alert.title}</div>
                    <div style={styles.alertMessage}>{alert.message}</div>
                  </div>
                  {alert.action && (
                    <Link to={alert.action.path} style={styles.alertActionBtn}>
                      {alert.action.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Upcoming Bookings */}
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Upcoming Bookings</h2>
              <Link to={servicesPath} style={styles.viewAllLink}>
                View All →
              </Link>
            </div>
            {serviceRequests.length > 0 ? (
              <div style={styles.bookingsList}>
                {serviceRequests.slice(0, 5).map(req => {
                  const property = userProperties.find(p => p.id === req.property_id);
                  return (
                    <div key={req.id} style={styles.bookingItem}>
                      <div style={styles.bookingDate}>
                        <div style={styles.bookingDay}>
                          {new Date(req.requested_date).getDate()}
                        </div>
                        <div style={styles.bookingMonth}>
                          {formatMonthShort(req.requested_date)}
                        </div>
                      </div>
                      <div style={styles.bookingDetails}>
                        <div style={styles.bookingProperty}>
                          {property?.property_name || 'Unknown Property'}
                        </div>
                        <div style={styles.bookingRooms}>
                          {req.room_ids?.length || 0} room(s) • {req.time_slot || 'No time specified'}
                        </div>
                      </div>
                      <span style={{
                        ...styles.bookingStatus,
                        background: req.status === 'confirmed' ? 'var(--color-info-bg)' : 'var(--color-warning-bg)',
                        color: req.status === 'confirmed' ? 'var(--color-info)' : 'var(--color-warning)'
                      }}>
                        {req.status}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.noData}>
                <p>No upcoming bookings</p>
                <Link to={servicesPath} style={styles.noDataBtn}>
                  + Create Booking
                </Link>
              </div>
            )}
          </div>
        </>
      )}

      {/* Batch Service Modal */}
      {showBatchServiceModal && selectedProperty && (
        <POSBatchServiceModal
          propertyId={selectedProperty.id}
          propertyName={selectedProperty.property_name}
          hospitalityTier={selectedProperty.hospitality_tier || 'assured'}
          onClose={() => setShowBatchServiceModal(false)}
          onSuccess={() => {
            setShowBatchServiceModal(false);
            // Reload service requests
            loadData();
          }}
        />
      )}
    </div>
  );
}

// Styles
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    minHeight: 'calc(100vh - 80px)'
  },
  dashboardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  accessBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    background: 'var(--color-border)',
    color: 'var(--color-sidebar-hover)',
    borderRadius: '20px',
    fontSize: 'var(--text-sm)',
    fontWeight: '500'
  },
  primaryActionBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 24px',
    background: POS_GOLD,
    color: 'var(--color-white)',
    border: 'none',
    borderRadius: 'var(--radius)',
    fontSize: 'var(--text-base)',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(189, 152, 47, 0.2)'
  },
  // Alerts section
  alertsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '20px'
  },
  alertCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'var(--color-white)',
    borderRadius: 'var(--radius)',
    borderLeft: '4px solid #dc2626',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  alertIcon: {
    fontSize: 'var(--text-3xl)',
    flexShrink: 0
  },
  alertContent: {
    flex: 1,
    minWidth: 0
  },
  alertTitle: {
    fontSize: 'var(--text-base)',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  alertMessage: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    marginTop: '2px'
  },
  alertActionBtn: {
    padding: '6px 12px',
    background: 'var(--color-bg)',
    color: '#334155',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--text-sm)',
    fontWeight: '500',
    textDecoration: 'none',
    whiteSpace: 'nowrap'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: 'var(--color-text-light)'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: POS_GOLD,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '600',
    color: '#1a1a2e',
    margin: 0,
    borderLeft: `4px solid ${POS_GOLD}`,
    paddingLeft: '16px'
  },
  subtitle: {
    fontSize: 'var(--text-base)',
    color: 'var(--color-text-light)',
    marginTop: '4px',
    paddingLeft: '20px'
  },
  headerRight: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  propertySelect: {
    padding: '10px 36px 10px 14px',
    border: '2px solid #e2e8f0',
    borderRadius: 'var(--radius)',
    fontSize: 'var(--text-base)',
    fontWeight: '500',
    color: '#1a1a2e',
    background: 'var(--color-white)',
    cursor: 'pointer',
    minWidth: '220px',
    appearance: 'none',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%239f9f9f\' d=\'M6 8L2 4h8z\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px'
  },
  statCard: {
    background: 'var(--color-white)',
    borderRadius: 'var(--radius-lg)',
    padding: '20px',
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e2e8f0'
  },
  statCardSuccess: {
    borderTop: '3px solid #16a34a'
  },
  statCardWarning: {
    borderTop: '3px solid #d97706'
  },
  statCardDanger: {
    borderTop: '3px solid #dc2626'
  },
  statValue: {
    fontSize: 'var(--text-5xl)',
    fontWeight: '700',
    color: '#1a1a2e',
    lineHeight: 1
  },
  statLabel: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)',
    marginTop: '8px',
    fontWeight: '500'
  },
  section: {
    background: 'var(--color-white)',
    borderRadius: 'var(--radius-lg)',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  sectionTitle: {
    fontSize: 'var(--text-xl)',
    fontWeight: '600',
    color: '#1a1a2e',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  viewAllLink: {
    fontSize: 'var(--text-md)',
    color: POS_GOLD,
    textDecoration: 'none',
    fontWeight: '500'
  },
  tierBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: 'var(--text-xs)',
    fontWeight: '600',
    color: 'var(--color-white)',
    textTransform: 'uppercase'
  },
  propertyStats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px'
  },
  propertyStatItem: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },
  propertyStatLabel: {
    fontSize: 'var(--text-md)',
    color: 'var(--color-text-light)'
  },
  propertyStatValue: {
    fontSize: 'var(--text-base)',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  contractBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-sm)',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  bookingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  bookingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '12px 16px',
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius)',
    border: '1px solid #e2e8f0'
  },
  bookingDate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: POS_GOLD,
    color: 'var(--color-white)',
    borderRadius: 'var(--radius)',
    padding: '8px 12px',
    minWidth: '50px'
  },
  bookingDay: {
    fontSize: 'var(--text-3xl)',
    fontWeight: '700',
    lineHeight: 1
  },
  bookingMonth: {
    fontSize: 'var(--text-xs)',
    textTransform: 'uppercase',
    marginTop: '2px'
  },
  bookingDetails: {
    flex: 1
  },
  bookingProperty: {
    fontSize: 'var(--text-base)',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  bookingRooms: {
    fontSize: 'var(--text-sm)',
    color: 'var(--color-text-light)',
    marginTop: '2px'
  },
  bookingStatus: {
    padding: '4px 12px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-sm)',
    fontWeight: '500',
    textTransform: 'capitalize'
  },
  noData: {
    textAlign: 'center',
    padding: '32px',
    color: 'var(--color-text-light)'
  },
  noDataBtn: {
    display: 'inline-block',
    marginTop: '12px',
    padding: '8px 16px',
    background: POS_GOLD,
    color: 'var(--color-white)',
    borderRadius: 'var(--radius)',
    textDecoration: 'none',
    fontSize: 'var(--text-md)',
    fontWeight: '500'
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px'
  },
  quickActionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '20px',
    background: 'var(--color-bg)',
    borderRadius: 'var(--radius-lg)',
    textDecoration: 'none',
    border: '1px solid #e2e8f0',
    transition: 'transform 0.15s, box-shadow 0.15s'
  },
  quickActionIcon: {
    fontSize: '28px'
  },
  quickActionLabel: {
    fontSize: 'var(--text-md)',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'var(--color-white)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
  },
  emptyStateIcon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  emptyStateBtn: {
    display: 'inline-block',
    marginTop: '16px',
    padding: '12px 24px',
    background: POS_GOLD,
    color: 'var(--color-white)',
    borderRadius: 'var(--radius)',
    textDecoration: 'none',
    fontSize: 'var(--text-base)',
    fontWeight: '600'
  }
};

// Add keyframe animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default POSDashboard;
